import React, { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  IconButton,
  TextField,
  Typography,
  Paper,
} from '@mui/material';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SendIcon from '@mui/icons-material/Send';

import { useAccess } from '../../hooks/useAccess';
import { useAuth } from '../../context/AuthContext';
import { LoginUserStatus } from '../../context/AuthContext';
import { useAiAssistant } from '../../context/AiAssistantContext';
import { useChatMutation } from '../../services/redux/api/aiAdminAPI';
import type { ChatMessage } from '../../context/AiAssistantContext';

import { useStyles } from './AiAssistant.styles';

export function AiAssistant() {
  const { t } = useTranslation();
  const { loginUserStatus } = useAuth();
  const { access } = useAccess();
  const { messages, addMessage, clearHistory } = useAiAssistant();
  const [chat, { isLoading }] = useChatMutation();
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const { classes } = useStyles();

  const canUseAssistant = access?.aiUse?.canUseAssistant ?? false;
  const isLoggedIn = loginUserStatus === LoginUserStatus.loggedIn;

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  React.useEffect(() => {
    if (isOpen && messages.length > 0) {
      scrollToBottom();
    }
  }, [isOpen, messages, scrollToBottom]);

  const handleSend = useCallback(async () => {
    const text = inputValue.trim();
    if (!text || isLoading) return;

    setInputValue('');
    addMessage({ role: 'user', content: text });

    const messagesForApi = [
      ...messages.map((m) => ({
        role: m.role === 'user' ? ('user' as const) : ('model' as const),
        content: m.content,
      })),
      { role: 'user' as const, content: text },
    ];

    try {
      const response = await chat({ messages: messagesForApi }).unwrap();

      addMessage({ role: 'assistant', content: response });
    } catch {
      addMessage({
        role: 'assistant',
        content: t('aiAssistant.error'),
      });
    }
  }, [inputValue, isLoading, messages, addMessage, chat, t]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend],
  );

  const handleClearHistory = useCallback(() => {
    clearHistory();
  }, [clearHistory]);

  if (!isLoggedIn || !canUseAssistant) {
    return null;
  }

  return (
    <>
      <Box
        className={classes.fab}
        onClick={() => setIsOpen(true)}
        sx={{ display: isOpen ? 'none' : 'flex' }}
        role="button"
        aria-label={t('aiAssistant.open')}>
        <SmartToyIcon />
      </Box>

      {isOpen && (
        <Paper elevation={8} className={classes.chatPanel}>
          <Box className={classes.chatHeader}>
            <Typography variant="text-md" className={classes.chatTitle}>
              {t('aiAssistant.title')}
            </Typography>
            <Box className={classes.headerActions}>
              <Button
                size="small"
                variant="text"
                onClick={handleClearHistory}
                sx={{ color: 'inherit', minWidth: 'auto' }}
                aria-label={t('aiAssistant.clearHistory')}>
                {t('aiAssistant.clearHistory')}
              </Button>
              <IconButton
                size="small"
                onClick={() => setIsOpen(false)}
                title={t('aiAssistant.collapse')}
                aria-label={t('aiAssistant.collapse')}
                sx={{ color: 'inherit' }}>
                <ExpandMoreIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>

          <Box className={classes.messagesContainer}>
            {messages.length === 0 && (
              <Typography
                variant="text-md"
                color="text.secondary"
                className={classes.emptyHint}>
                {t('aiAssistant.placeholder')}
              </Typography>
            )}
            {messages.map((msg: ChatMessage, idx: number) => (
              <Box
                key={idx}
                className={
                  msg.role === 'user'
                    ? classes.messageUser
                    : classes.messageAssistant
                }>
                <Typography variant="text-md" component="div">
                  {msg.content}
                </Typography>
              </Box>
            ))}
            {isLoading && (
              <Box className={classes.messageAssistant}>
                <Typography variant="text-md" color="text.secondary">
                  {t('aiAssistant.typing')}
                </Typography>
              </Box>
            )}
            <div ref={messagesEndRef} />
          </Box>

          <Box className={classes.inputContainer}>
            <Box className={classes.inputWrapper}>
              <TextField
                fullWidth
                multiline
                minRows={1}
                maxRows={4}
                placeholder={t('aiAssistant.inputPlaceholder')}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleKeyDown}
                disabled={isLoading}
                variant="outlined"
                size="small"
                className={classes.input}
                InputProps={{
                  sx: {
                    backgroundColor: 'background.paper',
                  },
                }}
              />
              <IconButton
                className={classes.sendButton}
                onClick={handleSend}
                disabled={!inputValue.trim() || isLoading}
                color="primary"
                aria-label={t('aiAssistant.send')}>
                <SendIcon />
              </IconButton>
            </Box>
          </Box>
        </Paper>
      )}
    </>
  );
}
